/**
 * Parental Consent Service
 *
 * Thin wrapper over the /auth/parental-consent/* endpoints. The GET verify
 * call is public (token-authenticated); the rest require an authenticated
 * session where the caller is the parent (matching email) or an admin.
 */

import { AxiosError } from 'axios';
import { apiClient } from './apiClient';
import { ApiResponse } from '../types';
import { ApiError } from '../types/auth';

export interface VerifyConsentResponse {
  childUserId: string;
  childName: string;
  grantedAt: string | null;
}

export interface ParentalConsentRecord {
  id: string;
  childUserId: string;
  parentEmail: string;
  action: 'REQUEST_SENT' | 'GRANTED' | 'REVOKED' | 'EXPIRED' | 'RESENT';
  ipAddress: string | null;
  userAgent: string | null;
  tokenHash: string | null;
  createdAt: string;
}

const handleApiError = (error: AxiosError): ApiError => {
  if (error.response) {
    const data = error.response.data as { message?: string; error?: string | { code?: string; message?: string } };
    let errorMessage = 'An error occurred';
    let errorCode = error.response.status.toString();
    if (data.message) {
      errorMessage = data.message;
    } else if (data.error) {
      if (typeof data.error === 'string') {
        errorMessage = data.error;
      } else if (data.error.message) {
        errorMessage = data.error.message;
        if (data.error.code) errorCode = data.error.code;
      }
    }
    return { code: errorCode, message: errorMessage, statusCode: error.response.status };
  }
  return { code: 'NETWORK', message: 'Network error', statusCode: 0 };
};

export const verifyConsentToken = async (token: string): Promise<VerifyConsentResponse> => {
  try {
    const response = await apiClient.get<ApiResponse<VerifyConsentResponse>>(
      `/auth/parental-consent/verify?token=${encodeURIComponent(token)}`,
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

export const requestConsentResend = async (childUserId: string): Promise<void> => {
  try {
    await apiClient.post('/auth/parental-consent/request', { childUserId });
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

export const withdrawConsent = async (childUserId: string, parentEmail: string): Promise<void> => {
  try {
    await apiClient.post('/auth/parental-consent/withdraw', { childUserId, parentEmail });
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

export const listConsentRecords = async (childUserId: string): Promise<ParentalConsentRecord[]> => {
  try {
    const response = await apiClient.get<ApiResponse<ParentalConsentRecord[]>>(
      `/auth/parental-consent/records/${childUserId}`,
    );
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};

export interface ChildSummary {
  id: string;
  email: string;
  username: string;
  fullName: string | null;
  parentConsentStatus: 'NONE' | 'PENDING' | 'GRANTED' | 'REVOKED';
  parentConsentAt: string | null;
  parentConsentExpiresAt: string | null;
  status: string;
  childVoiceRetentionConsent: boolean;
}

export const listMyChildren = async (): Promise<ChildSummary[]> => {
  try {
    const response = await apiClient.get<ApiResponse<ChildSummary[]>>('/auth/parental-consent/my-children');
    return response.data.data;
  } catch (error) {
    throw handleApiError(error as AxiosError);
  }
};
